import { User } from '../store/slices/authSlice';
import api from './api';
import {
  Auth0UserWithRoles,
  mapAuth0UserToAppUser,
  extractCompanyIdFromToken
} from '../utils/auth0Utils';

// This service is a wrapper around Auth0's authentication functionality
// It provides methods that match the original API but use Auth0 under the hood

// Define login response interface
export interface LoginResponse {
  user: User;
  access: string;
  refresh: string;
}

// Define the auth service
const createAuthService = () => {
  // We can't use the useAuth0 hook directly here because this is not a React component
  // Instead, we'll provide methods that should be called from components that use the useAuth0 hook

  return {
    // Get access token from Auth0
    getAccessToken: async (getTokenSilently: () => Promise<string>): Promise<string> => {
      return await getTokenSilently();
    },
    
    // Get company ID from the token
    getCompanyId: async (user: Auth0UserWithRoles): Promise<string | undefined> => {
      if (!user) {
        throw new Error('No user found');
      }
      
      return extractCompanyIdFromToken(user);
    },
    
    // Get user from Auth0
    getUser: async (user: Auth0UserWithRoles): Promise<User> => {
      if (!user) {
        throw new Error('No user found');
      }
      
      // Get default role from token
      const defaultRole = ((user as unknown) as { [key: string]: string[] })[`${import.meta.env.VITE_AUTH0_AUDIENCE}/roles`]?.[0] || 'user';
      const appUser = mapAuth0UserToAppUser(user, defaultRole);
      
      // If we don't have a company ID, log a warning
      if (!appUser.companyId) {
        console.warn('No company_id found in the user token. Multi-company filtering may not work correctly.');
      }
      
      return appUser;
    },
    
    // Update user profile in the backend
    updateProfile: async (userData: Partial<User>, getTokenSilently: () => Promise<string>): Promise<User> => {
      const token = await getTokenSilently();
      const response = await api.put('/users/profile/', userData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    },
    
    // Get available companies for the current user
    getUserCompanies: async (getTokenSilently: () => Promise<string>): Promise<Array<{ id: string, name: string }>> => {
      const token = await getTokenSilently();
      try {
        const response = await api.get('/users/companies/', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        return response.data;
      } catch (error) {
        console.error('Failed to fetch user companies:', error);
        return [];
      }
    },
    
    // Switch to a different company (for users with access to multiple companies)
    switchCompany: async (companyId: string, getTokenSilently: () => Promise<string>): Promise<void> => {
      const token = await getTokenSilently();
      try {
        await api.post('/users/switch-company/', { company_id: companyId }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Failed to switch company:', error);
        throw new Error('Failed to switch company');
      }
    },
  };
};

// Export the service creator function
export default createAuthService();